/**
 * SYS-B-02 (Phase 4 C2) — Contrôle de période / exercice ouvert.
 */
import type { ExerciceFiscal } from '@shared/types/ExerciceFiscal';
import { isExerciceOuvert } from '@domain/exercices/ExerciceOuvertGuard';

export type FenetreExercice = {
  code?: string;
  date_debut: string;
  date_fin: string;
  statut?: string;
};

export class DateHorsExerciceError extends Error {
  constructor(
    public readonly date: string,
    public readonly date_debut: string,
    public readonly date_fin: string,
  ) {
    super(`Date ${date} hors de l’exercice ouvert (${date_debut} → ${date_fin}). Écriture refusée.`);
    this.name = 'DateHorsExerciceError';
  }
}

export class AucunExerciceOuvertPourSaisieError extends Error {
  constructor() {
    super('Aucun exercice ouvert : impossible d’enregistrer une écriture.');
    this.name = 'AucunExerciceOuvertPourSaisieError';
  }
}

export function normaliserDateEcriture(date: string): string | null {
  const raw = date.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const fr = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  if (fr) return `${fr[3]}-${fr[2]}-${fr[1]}`;
  return null;
}

export function dateDansFenetre(dateIso: string, debut: string, fin: string): boolean {
  return dateIso >= debut && dateIso <= fin;
}

export function trouverExerciceOuvert(
  exercices: readonly (ExerciceFiscal | FenetreExercice)[],
): (ExerciceFiscal | FenetreExercice) | null {
  return (
    exercices.find((e) => {
      if ('statut' in e && e.statut) {
        if (e.statut === 'ouvert') return true;
        return isExerciceOuvert(e as ExerciceFiscal);
      }
      return false;
    }) ?? null
  );
}

export function motifDateHorsExercice(
  date: string,
  exercices: readonly (ExerciceFiscal | FenetreExercice)[],
): string | null {
  const iso = normaliserDateEcriture(date);
  if (!iso) return 'Date d’écriture invalide (attendu AAAA-MM-JJ).';
  const ouvert = trouverExerciceOuvert(exercices);
  if (!ouvert) return 'Aucun exercice ouvert : impossible d’enregistrer une écriture.';
  if (!dateDansFenetre(iso, ouvert.date_debut, ouvert.date_fin)) {
    return `Date ${iso} hors de l’exercice ouvert (${ouvert.date_debut} → ${ouvert.date_fin}). Écriture refusée.`;
  }
  return null;
}

export function assertDateDansExerciceOuvert(
  date: string,
  exercices: readonly (ExerciceFiscal | FenetreExercice)[],
): void {
  const iso = normaliserDateEcriture(date);
  if (!iso) throw new Error('Date d’écriture invalide (attendu AAAA-MM-JJ).');
  const ouvert = trouverExerciceOuvert(exercices);
  if (!ouvert) throw new AucunExerciceOuvertPourSaisieError();
  if (!dateDansFenetre(iso, ouvert.date_debut, ouvert.date_fin)) {
    throw new DateHorsExerciceError(iso, ouvert.date_debut, ouvert.date_fin);
  }
}
